import { useMemo } from 'react';
import { ClipboardList, Layers, TrendingUp, Truck, Users } from 'lucide-react';
import { CategoryDonut, DailyTrendChart, RankedBarChart } from '../charts/Charts';
import { ChartCard, StatTile } from './StatsParts';
import { Spinner } from '../ui/Spinner';
import { LoadError } from '../common/LoadError';
import { useLogbookEntries } from '../../hooks/useLogbookEntries';
import type { LogbookEntry } from '../../types/logbook';
import {
  dailyCounts, logbookByDriver, logbookByType, logbookByVehicle, resolveRange,
} from '../../utils/dashboardStats';

interface LogbookStatsPanelProps {
  from?: string;
  to?: string;
  periodLabel: string;
}

/**
 * Novedades del periodo. Se monta solo cuando la pestaña está activa, así que
 * un Administrador que nunca la abre no paga la carga de `/logbook-entries`.
 * El filtro va al servidor por `occurredAt` (cuándo OCURRIÓ, no cuándo se digitó).
 */
export function LogbookStatsPanel({ from, to, periodLabel }: LogbookStatsPanelProps) {
  const { entries, isLoading, error } = useLogbookEntries({ from, to });

  if (error && entries.length === 0) return <LoadError what="las novedades" error={error} />;
  if (isLoading) return <div className="dashboard-stats__loading"><Spinner size="lg" /></div>;
  return <LogbookStatsView entries={entries} from={from} to={to} periodLabel={periodLabel} />;
}

/** La vista pura: recibe las novedades ya cargadas. Separada para poder probarla con datos sintéticos. */
export function LogbookStatsView({ entries, from, to, periodLabel }: { entries: LogbookEntry[] } & LogbookStatsPanelProps) {
  const stats = useMemo(() => {
    const { start, end } = resolveRange(from, to, entries.map(e => e.occurredAt));
    const byType = logbookByType(entries);
    return {
      byType,
      topType: byType[0],
      trend: dailyCounts(entries, e => e.occurredAt, start, end),
      byVehicle: logbookByVehicle(entries, 6),
      byDriver: logbookByDriver(entries, 6),
    };
  }, [entries, from, to]);

  return (
    <div className="dashboard-stats">
      <div className="dashboard-stats__tiles">
        <StatTile label="Novedades en el periodo" value={entries.length} />
        <StatTile
          label="Tipo más frecuente"
          value={stats.topType?.label ?? '—'}
          hint={stats.topType ? `${stats.topType.value} registrada${stats.topType.value === 1 ? '' : 's'}` : undefined}
        />
        <StatTile label="Vehículos con novedades" value={new Set(entries.map(e => e.vehicleId).filter(Boolean)).size} />
        <StatTile label="Conductores con novedades" value={new Set(entries.map(e => e.driverId).filter(Boolean)).size} />
      </div>

      <section className="dashboard-page__charts">
        <ChartCard title={`Por tipo — ${periodLabel}`} icon={<Layers size={18} />} empty={entries.length === 0}>
          <CategoryDonut data={stats.byType} centerLabel="novedades" />
        </ChartCard>
        <ChartCard title={`Tendencia de novedades — ${periodLabel}`} icon={<TrendingUp size={18} />} empty={entries.length === 0}>
          <DailyTrendChart data={stats.trend} color="#3B82F6" seriesName="Novedades" />
        </ChartCard>
        <ChartCard title="Vehículos con más novedades" icon={<Truck size={18} />} empty={stats.byVehicle.length === 0}>
          <RankedBarChart data={stats.byVehicle} seriesName="Novedades" />
        </ChartCard>
        <ChartCard title="Conductores con más novedades" icon={<Users size={18} />} empty={stats.byDriver.length === 0}>
          <RankedBarChart data={stats.byDriver} seriesName="Novedades" />
        </ChartCard>
      </section>

      <p className="dashboard-stats__note">
        <ClipboardList size={13} /> Las novedades sin vehículo o sin conductor cuentan en el total y en el tipo, pero no aparecen en el ranking que les falta.
      </p>
    </div>
  );
}
